/**
 * 媒体元素输出通路。
 *
 * 把点击音离线渲染成满刻度 WAV，交给一组常驻 <audio> 播放，用于绕开某些内核给
 * Web Audio 渲染流单独加低增益的问题。拍点时刻仍由 AudioContext 时钟算出，
 * 这里只负责「再等多少毫秒出声」，因此不影响视觉与排期精度。
 */
import {
  CLICK,
  CLICK_TAIL,
  createNoiseBuffer,
  createSoftClipShaper,
  encodeWavBlob,
  scheduleClickNodes,
} from './clickVoice'

type Variant = 'accent' | 'normal'

/** 每种音色常驻的媒体元素个数；高 BPM 下前后点击会重叠，不能只留一个 */
const POOL_SIZE = 3
/** <audio>.play() 到真正出声有固定延迟，提前这么多毫秒发起以对齐排期时刻 */
const START_COMPENSATION_MS = 25
/**
 * 超过这个迟到量就干脆不发声：后台 / 息屏时 setTimeout 会被限流到秒级，
 * 晚半拍的点击比没更难听（与画面闪点对不上）；调度器重锚后下一拍会自然回到正轨。
 */
const MAX_LATE_MS = 60

/** 用与实时链路完全相同的节点图，离线渲染一次点击为 WAV */
async function renderClickBlob(sampleRate: number, isAccent: boolean): Promise<Blob> {
  type OfflineCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext
  const w = window as unknown as { OfflineAudioContext?: OfflineCtor; webkitOfflineAudioContext?: OfflineCtor }
  const Ctor = w.OfflineAudioContext ?? w.webkitOfflineAudioContext
  if (!Ctor) throw new Error('OfflineAudioContext 不可用')

  const duration = Math.max(CLICK.toneLength, CLICK.noiseLength) + CLICK_TAIL
  const octx = new Ctor(1, Math.ceil(sampleRate * duration), sampleRate)
  const shaper = createSoftClipShaper(octx)
  shaper.connect(octx.destination)
  scheduleClickNodes(octx, shaper, createNoiseBuffer(octx), 0, isAccent)
  return encodeWavBlob(await octx.startRendering())
}

function createElement(volume: number): HTMLAudioElement {
  const el = new Audio()
  el.preload = 'auto'
  el.volume = volume
  // 放进文档（不显示）：部分 WebView 内核不会播放游离于文档之外的媒体元素
  el.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none'
  el.setAttribute('aria-hidden', 'true')
  document.body.appendChild(el)
  return el
}

export class MediaClickOutput {
  private urls = new Map<Variant, string>()
  private pools = new Map<Variant, HTMLAudioElement[]>()
  private timers: number[] = []
  private cursors: Record<Variant, number> = { accent: 0, normal: 0 }
  private sampleRate = 0
  private volume = 1

  /** 两种音色都备齐才算可用（元素先建、采样渲好后再挂 src）；否则调用方应回落到 Web Audio 直出 */
  get ready(): boolean {
    return this.urls.size === 2 && this.pools.size === 2
  }

  /** 渲染并建立播放池；失败返回 false（调用方负责回落） */
  async prepare(sampleRate: number): Promise<boolean> {
    if (this.ready && this.sampleRate === sampleRate) return true
    this.dispose()
    // 元素必须在本函数的同步段里创建：iOS 只认可「在本次用户手势里诞生的元素」之后能 play()
    // （而异步渲染完成后再 new Audio() 就脱离了手势栈），所以先建空元素、渲完再挂 src。
    for (const variant of ['accent', 'normal'] as Variant[]) {
      this.pools.set(variant, Array.from({ length: POOL_SIZE }, () => createElement(this.volume)))
    }
    try {
      const [accent, normal] = await Promise.all([
        renderClickBlob(sampleRate, true),
        renderClickBlob(sampleRate, false),
      ])
      this.bind('accent', accent)
      this.bind('normal', normal)
      this.sampleRate = sampleRate
      return true
    } catch {
      this.dispose()
      return false
    }
  }

  /**
   * 延时播放一次点击。
   * @param delayMs 距该拍点的剩余时间（由音频时钟算出，负值视为立即）
   */
  play(isAccent: boolean, delayMs: number): void {
    const variant: Variant = isAccent ? 'accent' : 'normal'
    if (!this.ready) return
    const wait = Math.max(0, delayMs - START_COMPENSATION_MS)
    // 定时器实际到点的时刻；晚于它 MAX_LATE_MS 就丢弃
    const due = performance.now() + wait
    const timer = window.setTimeout(() => {
      this.timers = this.timers.filter((id) => id !== timer)
      this.fire(variant, due)
    }, wait)
    this.timers.push(timer)
  }

  /** 丢弃尚未到点的排期并停止所有正在播的元素（切换通路 / 停止播放时调用） */
  cancel(): void {
    this.timers.forEach((id) => window.clearTimeout(id))
    this.timers = []
    for (const pool of this.pools.values()) {
      for (const el of pool) {
        el.pause()
        try {
          el.currentTime = 0
        } catch {
          // 尚未可 seek 时忽略
        }
      }
    }
  }

  setVolume(volume: number): void {
    this.volume = volume
    for (const pool of this.pools.values()) {
      for (const el of pool) el.volume = volume
    }
  }

  /** 释放解码资源与 objectURL（通路切回 Web Audio 或页面卸载时调用） */
  dispose(): void {
    this.cancel()
    for (const pool of this.pools.values()) {
      for (const el of pool) el.remove()
    }
    this.pools.clear()
    for (const url of this.urls.values()) URL.revokeObjectURL(url)
    this.urls.clear()
    this.sampleRate = 0
  }

  private bind(variant: Variant, blob: Blob): void {
    const url = URL.createObjectURL(blob)
    this.urls.set(variant, url)
    for (const el of this.pools.get(variant) ?? []) el.src = url
  }

  /** 取一个空闲元素，全忙则按轮转取（重叠播放时最旧的那个已经听不出来了） */
  private fire(variant: Variant, due: number): void {
    if (performance.now() - due > MAX_LATE_MS) return // 被限流拖晚太多，丢掉这一拍
    const pool = this.pools.get(variant)
    if (!pool?.length) return
    const index = this.cursors[variant] % pool.length
    this.cursors[variant] = index + 1
    const el = pool.find((item) => item.paused || item.ended) ?? pool[index]!
    try {
      el.currentTime = 0
    } catch {
      // 还没准备好，下面 play() 会从头播
    }
    void el.play().catch(() => undefined) // 自动播放策略未放开时保持静默，由上层提示用户
  }
}
